// src/services/uploadService.js
// Provides functions to list, upload and delete student test uploads.
// These call the backend API which will handle OCI interaction. Replace the routes
// if you decide on direct OCI client-side upload / signed URLs.

import api, { API_URL } from './api';
import { UPLOAD_ENDPOINTS } from '../config/Config';

/** URL to open/download a student upload PDF via backend proxy (avoids direct OCI URL in browser). */
export const getStudentUploadPdfContentUrl = (uploadId) =>
  `${API_URL}/student-uploads/${uploadId}/pdf-content/`;

/**
 * Fetch student upload PDF with auth (api client sends credentials) and open in new tab.
 * Use this instead of href to pdf-content so the request is authenticated.
 */
export const openStudentUploadPdf = async (uploadId, filename = 'worksheet.pdf') => {
  const res = await api.get(`/student-uploads/${uploadId}/pdf-content/`, { responseType: 'arraybuffer' });
  const blob = new Blob([res.data], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

/** Same as openStudentUploadPdf but for TeacherUpload rows (e.g. roster / teacher-managed worksheet). */
export const openTeacherUploadPdf = async (uploadId, filename = 'worksheet.pdf') => {
  const res = await api.get(`/teacher-uploads/${uploadId}/pdf-content/`, { responseType: 'arraybuffer' });
  const blob = new Blob([res.data], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

const endpointForRole = (role) => {
  // Convert role to uppercase for case-insensitive comparison
  const roleUpper = role?.toUpperCase();
  switch (roleUpper) {
    case 'TEACHER':
      return UPLOAD_ENDPOINTS.TEACHER;
    case 'STUDENT':
      return UPLOAD_ENDPOINTS.STUDENT;
    case 'ADMIN':
      return UPLOAD_ENDPOINTS.ADMIN;
    default:
      return UPLOAD_ENDPOINTS.STUDENT; // Default to student endpoint instead of teacher
  }
};

// GET list uploads
export const listUploads = async ({ studentId, testId, role = 'student', teacherManagedStudentId }) => {
  const params = { testId };
  if (teacherManagedStudentId != null) {
    params.teacherManagedStudentId = teacherManagedStudentId;
  } else {
    params.studentId = studentId;
  }
  const { data } = await api.get(endpointForRole(role), {
    params,
  });

  // DRF returns paginated object {results: [...]} – fallback to data if already array
  const items = Array.isArray(data) ? data : data.results || [];

  // Convert snake_case keys to camelCase for frontend consistency
  return items.map((item) => ({
    ...item,
    uploadedAt: item.uploaded_at,
    hasTeacherAnnotations: item.has_teacher_annotations ?? false,
  }));
};

// POST multipart/form-data => /api/student-uploads/
export const uploadFile = async ({
  studentId,
  testId,
  file,
  onUploadProgress,
  role = 'student',
  teacherManagedStudentId,
}) => {
  const form = new FormData();
  if (teacherManagedStudentId != null) {
    form.append('teacherManagedStudentId', String(teacherManagedStudentId));
  } else {
    form.append('studentId', studentId);
  }
  form.append('testId', testId);
  form.append('file', file);

  const { data } = await api.post(endpointForRole(role), form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress,
  });
  return data; // uploaded file metadata
};

// DELETE /api/student-uploads/:id/
export const deleteUpload = async (uploadId, role='student') => {
  const endpoint = endpointForRole(role);
  const { data } = await api.delete(`${endpoint}${uploadId}/`);
  return data;
};

const uploadService = {
  listUploads,
  uploadFile,
  deleteUpload,
  getStudentUploadPdfContentUrl,
  openStudentUploadPdf,
  openTeacherUploadPdf,
};
export default uploadService;
